import type {
  GameAssetBinding,
  GameClamp,
  GameCondition,
  GameDefinition,
  GameGate,
  GameRule,
  GameStateFieldSchema,
  GameStateMutableBy,
  GameStateValueType,
} from "./game-definition.js";
import {
  isRoqeetGameDocument,
  roqeetJsonToGameDefinition,
  type RoqeetGameDocument,
} from "./roqeet-game-document.js";

export type GameDiagnosticSeverity = "info" | "warning" | "error";

export interface GameDiagnostic {
  severity: GameDiagnosticSeverity;
  code: string;
  message: string;
  path?: string;
}

export interface NormalizedGameDefinition extends GameDefinition {
  stateSchema: GameStateFieldSchema[];
  mutableKeys: string[];
  clamps: GameClamp[];
  diagnostics: GameDiagnostic[];
  statePermissions: {
    aiMutableKeys: string[];
    engineKeys: string[];
    readonlyKeys: string[];
    derivedKeys: string[];
    clamps: GameClamp[];
  };
}

export function normalizeGameDefinition(input: GameDefinition | RoqeetGameDocument): NormalizedGameDefinition {
  const diagnostics: GameDiagnostic[] = [];
  const source = isRoqeetGameDocument(input) ? roqeetJsonToGameDefinition(input) : input;
  const def = clone(source);
  const legacyMutable = new Set((def.mutableKeys ?? []).filter(Boolean));
  const schema = normalizeStateSchema(def, legacyMutable, diagnostics);
  const schemaByKey = new Map(schema.map((field) => [field.key, field]));

  for (const [key, value] of Object.entries(def.initialState ?? {})) {
    const field = schemaByKey.get(key);
    if (!field) continue;
    if (!matchesType(value, field)) {
      diagnostics.push({
        severity: "error",
        code: "GAME_STATE_TYPE_MISMATCH",
        message: `Initial state key "${key}" does not match stateSchema type "${field.valueType}".`,
        path: `initialState.${key}`,
      });
    }
    if (field.default !== undefined && JSON.stringify(field.default) !== JSON.stringify(value)) {
      diagnostics.push({
        severity: "warning",
        code: "GAME_SCHEMA_DEFAULT_DIFFERS",
        message: `State schema default for "${key}" differs from initialState.`,
        path: `stateSchema.${key}.default`,
      });
    }
  }

  for (const key of legacyMutable) {
    const field = schemaByKey.get(key);
    if (!field) {
      diagnostics.push({
        severity: "info",
        code: "GAME_LEGACY_MUTABLE_UNKNOWN",
        message: `Legacy mutableKeys includes "${key}", but no stateSchema field exists. It is ignored by runtime permissions.`,
        path: "mutableKeys",
      });
      continue;
    }
    if (field.mutableBy !== "ai") {
      diagnostics.push({
        severity: systemKeySet.has(key) ? "warning" : "info",
        code: "GAME_LEGACY_MUTABLE_CONFLICT",
        message: `Legacy mutableKeys includes "${key}", but stateSchema marks it as ${field.derived ? "derived/engine-controlled" : field.mutableBy}. The runtime will not allow AI to change it.`,
        path: "mutableKeys",
      });
    }
  }

  const clamps = mergeClamps(schemaClamps(schema), def.clamps ?? []);
  validateClamps(clamps, schemaByKey, diagnostics);
  validateConditions("rules", (def.rules ?? []).flatMap((rule) => rule.when ?? []), schemaByKey, diagnostics);
  validateRuleEffects(def.rules ?? [], schemaByKey, diagnostics);
  validateConditions("initRules", (def.initRules ?? []).flatMap((rule) => rule.when ?? []), schemaByKey, diagnostics);
  validateRuleEffects(def.initRules ?? [], schemaByKey, diagnostics);
  validateConditions("endWhen", def.endWhen ?? [], schemaByKey, diagnostics);
  validateGates(def.gates ?? [], schemaByKey, diagnostics);
  validateHudRefs(def, schemaByKey, diagnostics);
  validateAssetBindings(def.assetBindings ?? [], new Set((def.assets ?? []).map((asset) => asset.key)), schemaByKey, diagnostics);
  validateTemplates(def, schemaByKey, diagnostics);

  if (def.dice?.dcKey) {
    diagnostics.push({
      severity: schemaByKey.has(def.dice.dcKey) ? "info" : "warning",
      code: "GAME_DICE_DCKEY_LEGACY",
      message: `Dice DC key "${def.dice.dcKey}" is legacy. Prefer inline roll syntax: @roll d20 dc=12.`,
      path: "dice.dcKey",
    });
  }

  if (def.stateSummaryTemplate && /@\(\s*([\p{L}_][\p{L}\p{N}_.-]*)\s*\)/u.test(def.stateSummaryTemplate)) {
    def.stateSummaryTemplate = normalizeTemplateSyntax(def.stateSummaryTemplate);
    diagnostics.push({
      severity: "info",
      code: "GAME_TEMPLATE_LEGACY_NORMALIZED",
      message: "Legacy @(key) template syntax was normalized to {{key}}.",
      path: "stateSummaryTemplate",
    });
  }

  const aiMutableKeys = schema.filter((field) => field.mutableBy === "ai" && !field.derived).map((field) => field.key);
  const engineKeys = schema.filter((field) => field.mutableBy === "engine").map((field) => field.key);
  const readonlyKeys = schema.filter((field) => field.mutableBy === "readonly").map((field) => field.key);
  const derivedKeys = schema.filter((field) => field.derived).map((field) => field.key);

  return {
    ...def,
    stateSchema: schema,
    mutableKeys: aiMutableKeys,
    clamps,
    diagnostics,
    statePermissions: {
      aiMutableKeys,
      engineKeys,
      readonlyKeys,
      derivedKeys,
      clamps,
    },
  };
}

function normalizeStateSchema(
  def: GameDefinition,
  legacyMutable: Set<string>,
  diagnostics: GameDiagnostic[],
): GameStateFieldSchema[] {
  const out: GameStateFieldSchema[] = [];
  const seen = new Set<string>();

  for (const raw of def.stateSchema ?? []) {
    if (!raw.key?.trim()) continue;
    const key = raw.key.trim();
    if (seen.has(key)) {
      diagnostics.push({
        severity: "warning",
        code: "GAME_STATE_SCHEMA_DUPLICATE",
        message: `State schema has duplicate key "${key}". The first field wins.`,
        path: "stateSchema",
      });
      continue;
    }
    seen.add(key);
    out.push(normalizeField({ ...raw, key }, legacyMutable, diagnostics));
  }

  for (const [key, value] of Object.entries(def.initialState ?? {})) {
    if (seen.has(key)) continue;
    out.push(inferField(key, value, legacyMutable));
    seen.add(key);
    diagnostics.push({
      severity: "warning",
      code: "GAME_INITIAL_STATE_SCHEMA_INFERRED",
      message: `Initial state key "${key}" has no state schema field. A safe schema field was inferred.`,
      path: `initialState.${key}`,
    });
  }

  return out;
}

function normalizeField(
  field: GameStateFieldSchema,
  _legacyMutable: Set<string>,
  diagnostics: GameDiagnostic[],
): GameStateFieldSchema {
  const explicitMutableBy = field.mutableBy;
  const mutableBy = normalizeMutableBy(field);
  if (field.mutableBy === undefined && field.mutable !== undefined) {
    diagnostics.push({
      severity: "info",
      code: "GAME_MUTABLE_ALIAS_CONVERTED",
      message: `State field "${field.key}" legacy mutable was converted to mutableBy "${mutableBy}".`,
      path: `stateSchema.${field.key}`,
    });
  }
  const safeSystem = (systemKeySet.has(field.key) || diceEngineKeySet.has(field.key)) && explicitMutableBy !== "ai";
  const normalized: GameStateFieldSchema = {
    ...field,
    valueType: normalizeValueType(field.valueType),
    mutableBy: safeSystem ? "engine" : mutableBy,
  };
  if (safeSystem) normalized.derived = true;
  if (normalized.derived && normalized.mutableBy === "ai" && explicitMutableBy !== "ai") {
    normalized.mutableBy = "engine";
  }
  if (normalized.derived && normalized.mutableBy === "ai") {
    diagnostics.push({
      severity: "warning",
      code: "GAME_SYSTEM_KEY_AI_MUTABLE",
      message: `Derived state "${normalized.key}" is engine-controlled and should not be AI-mutable.`,
      path: `stateSchema.${normalized.key}`,
    });
  }
  return normalized;
}

function normalizeMutableBy(field: GameStateFieldSchema): GameStateMutableBy {
  if (field.mutableBy === "ai" || field.mutableBy === "engine" || field.mutableBy === "readonly") return field.mutableBy;
  if (field.mutable === true) return "ai";
  if (field.derived) return "engine";
  return "readonly";
}

function inferField(key: string, value: unknown, legacyMutable: Set<string>): GameStateFieldSchema {
  const system = systemField(key, value);
  if (system) return system;
  const mutableBy: GameStateMutableBy = legacyMutable.has(key) || visualMutableKeys.has(key) ? "ai" : "readonly";
  if (key === "inventory") return { key, valueType: "string[]", default: [], mutableBy };
  return {
    key,
    valueType: inferValueType(value),
    default: cloneDefault(value),
    mutableBy,
  };
}

function systemField(key: string, value: unknown): GameStateFieldSchema | undefined {
  if (systemKeySet.has(key)) {
    return { key, valueType: "boolean", default: typeof value === "boolean" ? value : false, mutableBy: "engine", mutable: false, derived: true };
  }
  if (key === "lastRoll") {
    return { key, valueType: "number", default: typeof value === "number" ? value : 0, mutableBy: "engine", mutable: false, derived: true, transient: true };
  }
  if (key === "lastRollNotation") {
    return { key, valueType: "string", default: typeof value === "string" ? value : "", mutableBy: "engine", mutable: false, derived: true, transient: true };
  }
  if (key === "lastRollSuccess") {
    return { key, valueType: "boolean", default: typeof value === "boolean" ? value : false, mutableBy: "engine", mutable: false, derived: true, transient: true };
  }
  if (readonlyNumberKeys.has(key) || /^max[A-Z]/.test(key) || /Goal$/.test(key)) {
    return { key, valueType: "number", default: typeof value === "number" ? value : 0, mutableBy: "readonly" };
  }
  if (key === "expression") return { key, valueType: "string", default: typeof value === "string" ? value : "neutral", mutableBy: "ai" };
  if (visualMutableKeys.has(key)) return { key, valueType: "string", default: typeof value === "string" ? value : "", mutableBy: "ai" };
  return undefined;
}

function inferValueType(value: unknown): GameStateValueType {
  if (typeof value === "number") return "number";
  if (typeof value === "boolean") return "boolean";
  if (Array.isArray(value)) return value.every((item) => typeof item === "string") ? "string[]" : "array";
  if (value && typeof value === "object") return "object";
  return "string";
}

function cloneDefault(value: unknown): GameStateFieldSchema["default"] {
  if (typeof value === "number" || typeof value === "string" || typeof value === "boolean") return value;
  if (Array.isArray(value)) return clone(value);
  if (value && typeof value === "object") return clone(value as Record<string, unknown>);
  return undefined;
}

function normalizeValueType(valueType: GameStateValueType): GameStateValueType {
  return valueType === "list" ? "array" : valueType;
}

function matchesType(value: unknown, field: GameStateFieldSchema): boolean {
  switch (normalizeValueType(field.valueType)) {
    case "number":
      return typeof value === "number" && Number.isFinite(value);
    case "boolean":
      return typeof value === "boolean";
    case "string":
      return typeof value === "string";
    case "enum":
      return typeof value === "string" && (!field.values?.length || field.values.includes(value));
    case "string[]":
      return Array.isArray(value) && value.every((item) => typeof item === "string");
    case "array":
      return Array.isArray(value);
    case "object":
      return !!value && typeof value === "object" && !Array.isArray(value);
    case "list":
      return Array.isArray(value);
  }
}

function schemaClamps(schema: GameStateFieldSchema[]): GameClamp[] {
  return schema
    .filter((field) => field.min !== undefined || field.max !== undefined)
    .map((field) => ({
      key: field.key,
      ...(field.min !== undefined ? { min: field.min } : {}),
      ...(field.max !== undefined ? { max: field.max } : {}),
    }));
}

function mergeClamps(...groups: GameClamp[][]): GameClamp[] {
  const map = new Map<string, GameClamp>();
  for (const clamp of groups.flat()) {
    const prev = map.get(clamp.key) ?? { key: clamp.key };
    map.set(clamp.key, {
      key: clamp.key,
      ...(prev.min !== undefined ? { min: prev.min } : {}),
      ...(prev.max !== undefined ? { max: prev.max } : {}),
      ...(clamp.min !== undefined ? { min: clamp.min } : {}),
      ...(clamp.max !== undefined ? { max: clamp.max } : {}),
    });
  }
  return [...map.values()];
}

function validateClamps(clamps: GameClamp[], schemaByKey: Map<string, GameStateFieldSchema>, diagnostics: GameDiagnostic[]): void {
  for (const clamp of clamps) {
    const field = schemaByKey.get(clamp.key);
    if (!field) {
      diagnostics.push({ severity: "warning", code: "GAME_CLAMP_UNKNOWN_KEY", message: `Clamp references missing key "${clamp.key}".`, path: "clamps" });
      continue;
    }
    if (normalizeValueType(field.valueType) !== "number") {
      diagnostics.push({ severity: "warning", code: "GAME_CLAMP_NON_NUMBER", message: `Clamp key "${clamp.key}" is not declared as number.`, path: "clamps" });
    }
    if (clamp.min !== undefined && clamp.max !== undefined && clamp.min > clamp.max) {
      diagnostics.push({ severity: "error", code: "GAME_CLAMP_INVALID_BOUNDS", message: `Clamp key "${clamp.key}" has min greater than max.`, path: "clamps" });
    }
  }
}

function validateConditions(
  field: string,
  conditions: GameCondition[],
  schemaByKey: Map<string, GameStateFieldSchema>,
  diagnostics: GameDiagnostic[],
): void {
  for (const condition of conditions) {
    if (condition.key && !schemaByKey.has(condition.key)) {
      diagnostics.push({ severity: "warning", code: "GAME_CONDITION_UNKNOWN_KEY", message: `${field} condition references missing key "${condition.key}".`, path: field });
    }
  }
}

function validateRuleEffects(rules: GameRule[], schemaByKey: Map<string, GameStateFieldSchema>, diagnostics: GameDiagnostic[]): void {
  for (const rule of rules) {
    for (const action of rule.then ?? []) {
      if (!schemaByKey.has(action.key)) {
        diagnostics.push({ severity: "warning", code: "GAME_RULE_EFFECT_UNKNOWN_KEY", message: `Rule effect references missing key "${action.key}".`, path: "rules" });
      }
    }
  }
}

function validateGates(gates: GameGate[], schemaByKey: Map<string, GameStateFieldSchema>, diagnostics: GameDiagnostic[]): void {
  for (const gate of gates) {
    validateConditions("gates", gate.when ?? [], schemaByKey, diagnostics);
    for (const key of collectTemplateKeys(gate.inject)) {
      if (!schemaByKey.has(key)) {
        diagnostics.push({ severity: "warning", code: "GAME_GATE_TEMPLATE_UNKNOWN_KEY", message: `Gate inject string uses unknown key "${key}".`, path: "gates" });
      }
    }
  }
}

function validateHudRefs(def: GameDefinition, schemaByKey: Map<string, GameStateFieldSchema>, diagnostics: GameDiagnostic[]): void {
  for (const view of [def.hud, def.view]) {
    for (const bar of view?.statBars ?? []) {
      for (const key of [bar.valueKey, bar.maxKey].filter(Boolean) as string[]) {
        if (!schemaByKey.has(key)) diagnostics.push({ severity: "warning", code: "GAME_HUD_UNKNOWN_KEY", message: `HUD references missing state key "${key}".`, path: "hud" });
      }
    }
    for (const panel of view?.statPanel ?? []) {
      if (panel.key && !schemaByKey.has(panel.key)) diagnostics.push({ severity: "warning", code: "GAME_HUD_UNKNOWN_KEY", message: `HUD references missing state key "${panel.key}".`, path: "hud" });
    }
    if (view?.inventoryKey && !schemaByKey.has(view.inventoryKey)) {
      diagnostics.push({ severity: "warning", code: "GAME_HUD_UNKNOWN_KEY", message: `HUD references missing state key "${view.inventoryKey}".`, path: "hud" });
    }
  }
}

function validateAssetBindings(
  bindings: GameAssetBinding[],
  assetKeys: Set<string>,
  schemaByKey: Map<string, GameStateFieldSchema>,
  diagnostics: GameDiagnostic[],
): void {
  for (const binding of bindings) {
    if (binding.assetKey && !assetKeys.has(binding.assetKey)) {
      diagnostics.push({ severity: "error", code: "GAME_ASSET_BINDING_MISSING_ASSET", message: `Asset binding "${binding.id}" references missing asset "${binding.assetKey}".`, path: "assetBindings" });
    }
    validateConditions("assetBindings", binding.when ?? [], schemaByKey, diagnostics);
  }
}

function validateTemplates(def: GameDefinition, schemaByKey: Map<string, GameStateFieldSchema>, diagnostics: GameDiagnostic[]): void {
  if (!def.stateSummaryTemplate) return;
  for (const key of collectTemplateKeys(def.stateSummaryTemplate)) {
    if (!schemaByKey.has(key)) {
      diagnostics.push({ severity: "warning", code: "GAME_TEMPLATE_UNKNOWN_KEY", message: `State summary template uses unknown key "${key}".`, path: "stateSummaryTemplate" });
    }
  }
}

function collectTemplateKeys(template: string): string[] {
  const keys: string[] = [];
  let m: RegExpExecArray | null;
  const braces = /\{\{\s*([\w.-]+)\s*\}\}/g;
  while ((m = braces.exec(template)) !== null) keys.push(m[1]!);
  const legacy = /@\(\s*([\p{L}_][\p{L}\p{N}_.-]*)\s*\)/gu;
  while ((m = legacy.exec(template)) !== null) keys.push(m[1]!);
  return keys;
}

function normalizeTemplateSyntax(template: string): string {
  return template.replace(/@\(\s*([\p{L}_][\p{L}\p{N}_.-]*)\s*\)/gu, "{{$1}}");
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

const systemKeySet = new Set(["completed", "failed", "ended"]);
const diceEngineKeySet = new Set(["lastRoll", "lastRollNotation", "lastRollSuccess"]);
const readonlyNumberKeys = new Set(["maxTrust", "maxDanger", "clueGoal", "progressGoal"]);
const visualMutableKeys = new Set(["location", "scene", "background", "expression"]);
